/**
 * Safely extract an error message from an Axios error response.
 * Handles Pydantic validation error arrays ({type, loc, msg, input}).
 */
export function extractErrorMessage(err, fallback = "An error occurred.") {
  const detail = err?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((d) => (typeof d === "string" ? d : d.msg || JSON.stringify(d))).join("; ");
  }
  return fallback;
}

/**
 * Return today's date as YYYY-MM-DD using the local (browser) clock.
 * Using toISOString() would return UTC date which is wrong for UTC+8.
 */
export function getLocalToday() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function getLocalTomorrow() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/**
 * Format an ISO date string as MM-DD-YYYY for display.
 */
export function formatDate(value) {
  if (!value) return "";
  const dateStr = String(value).slice(0, 10);
  const [year, month, day] = dateStr.split("-");
  return `${month}-${day}-${year}`;
}

/**
 * Format an ISO datetime string as MM-DD-YYYY HH:MM AM/PM for display.
 */
export function formatDateTime(value) {
  if (!value) return "";
  return new Date(value).toLocaleString("en-PH", {
    timeZone: "Asia/Manila",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Format a number as Philippine Peso currency.
 */
export function formatCurrency(value) {
  if (value == null || value === "") return "";
  return Number(value).toLocaleString("en-PH", {
    style: "currency",
    currency: "PHP",
  });
}

/**
 * Calculate the ageing (days) for a pending job order based on the original activation date.
 * Ageing = days from ageing_origin_date to today (in browser local date).
 * Caps at 10 (will never show beyond Day 10 for pending jobs).
 * Returns the ageing number, or null if not applicable (not pending, or ageing_origin_date missing).
 */
export function calculatePendingAgeing(jobOrder, todayDate) {
  if (!jobOrder || jobOrder.jo_status !== "Pending" || !jobOrder.ageing_origin_date) {
    return null;
  }

  const today = todayDate || getLocalToday();

  // Only show ageing superscript for today's and future pending JOs.
  // If the JO's job_date is before today, the user is viewing a historical date
  // and should see plain "Pending" with no superscript.
  const jobDateStr = String(jobOrder.job_date).slice(0, 10);
  if (jobDateStr < today) {
    return null;
  }

  const originStr = String(jobOrder.ageing_origin_date).slice(0, 10);

  // Ageing = job_date (the date being viewed) minus original activation date.
  // Using job_date instead of actual "today" is critical: when the user navigates
  // to a future date (e.g. viewing tomorrow's JOs today), the ageing must reflect
  // how many days old the JO will be ON THAT future date, not how old it is right now.
  // Example: origin=Mar 18, job_date=Mar 27 → ageing=9, regardless of actual clock date.
  const [jobDateYear, jobDateMonth, jobDateDay] = jobDateStr.split("-").map(Number);
  const [originYear, originMonth, originDay] = originStr.split("-").map(Number);

  const jobDateObj = new Date(jobDateYear, jobDateMonth - 1, jobDateDay);
  const originObj = new Date(originYear, originMonth - 1, originDay);

  // Math.round guards against any floating-point edge case (DST etc.)
  const ageingDays = Math.round((jobDateObj - originObj) / (1000 * 60 * 60 * 24));

  if (ageingDays < 0) return null; // origin after job_date, shouldn't happen

  // Cap at 10 (Day 11+ should never appear as pending due to auto-cancel in rollover)
  return Math.min(ageingDays, 10);
}

/**
 * Return a Tailwind text-color class for a pending ageing day count.
 * 0-1  → green  (fresh / on-time)
 * 2-6  → inherit (no extra class; badge color applies)
 * 7-10 → red    (overdue / critical)
 */
export function getAgeingColor(ageing) {
  if (ageing <= 1) return "text-green-600";
  if (ageing >= 7) return "text-red-600";
  return "";
}
